<template>
	<div class="campaign-response-report-page">
		<div class="report-container container">
			<div class="page-header d-flex justify-content-between align-items-center my-4 flex-wrap gap-3">
				<div class="d-flex align-items-center">
					<router-link :to="backRoute" class="back-btn me-3" title="Back to campaign" aria-label="Back to campaign">
						<i class="fad fa-arrow-left"></i>
					</router-link>
					<div>
						<h2 class="page-title">{{ report.campaign.name || 'Response Report' }}</h2>
						<p class="page-subtitle">
							Unique URL / QR responses from mailed recipients
							<span v-if="report.campaign.utm_campaign"> · utm_campaign <code>{{ report.campaign.utm_campaign }}</code></span>
						</p>
					</div>
				</div>
				<div class="report-actions d-flex align-items-center gap-3">
					<div
						v-if="!loading && report.campaign.id"
						class="form-check form-switch mb-0 slack-toggle"
						:title="report.campaign.slack_configured ? 'Post a Slack message for every visit from now on' : 'Set the Slack webhook in Settings → Short Links first'"
					>
						<input
							class="form-check-input"
							type="checkbox"
							id="reportSlackToggle"
							:checked="report.campaign.shortlink_slack_notify"
							:disabled="savingSlack || (!report.campaign.slack_configured && !report.campaign.shortlink_slack_notify)"
							@change="toggleSlack($event.target.checked)"
						/>
						<label class="form-check-label" for="reportSlackToggle"><i class="fab fa-slack me-1"></i>Slack on visit</label>
					</div>
					<button type="button" class="back-btn" title="Refresh" aria-label="Refresh report" @click="loadReport" :disabled="loading">
						<i :class="['fad', 'fa-sync', { 'fa-spin': loading }]"></i>
					</button>
				</div>
			</div>

			<div v-if="loading" class="loading-state">
				<div class="spinner-border text-primary" role="status"></div>
				<p class="mt-3 text-muted">Loading response report...</p>
			</div>

			<div v-else class="report-content">
				<div v-if="!report.campaign.unique_url_enabled" class="alert alert-warning">
					Unique URL tracking is turned off for this campaign. {{ isRegular ? 'Postcards sent from now on' : 'Runs created from now on' }} will not get tracked links.
				</div>

				<div class="overview-grid">
					<div class="metric-card">
						<div class="metric-value">{{ totals.mailed }}</div>
						<div class="metric-label">Mailed (tracked)</div>
					</div>
					<div class="metric-card">
						<div class="metric-value text-success">{{ totals.visited }}</div>
						<div class="metric-label">
							Visited
							<span v-if="totals.total_visits > totals.visited" class="text-muted">· {{ totals.total_visits }} visits in total</span>
						</div>
					</div>
					<div class="metric-card">
						<div class="metric-value text-primary">{{ formatPercent(totals.response_rate) }}</div>
						<div class="metric-label">Response Rate</div>
					</div>
					<div class="metric-card">
						<div class="metric-value">
							<span class="text-info">{{ totals.visited_qr }}</span>
							<span class="metric-divider">/</span>
							<span>{{ totals.visited_typed }}</span>
						</div>
						<div class="metric-label">
							QR scan / Typed link
							<span v-if="totals.visited_unknown" class="text-muted">· {{ totals.visited_unknown }} unknown</span>
						</div>
					</div>
					<div class="metric-card">
						<div class="metric-value">{{ totals.avg_days_to_visit === null ? '–' : totals.avg_days_to_visit }}</div>
						<div class="metric-label">Avg days from {{ isRegular ? 'expected delivery' : 'delivery' }} to visit</div>
					</div>
				</div>

				<div v-if="!isRegular" class="report-card mt-4">
					<div class="report-card-header">
						<h3 class="report-card-title">By Run</h3>
						<span class="report-card-meta">Only pieces that went out in the mail; internal always-on copies are excluded.</span>
					</div>
					<div v-if="report.runs.length" class="activity-table">
						<div class="table-header runs-grid">
							<div class="header-cell">Run</div>
							<div class="header-cell">Sent</div>
							<div class="header-cell text-center">Mailed</div>
							<div class="header-cell text-center">Visited</div>
							<div class="header-cell text-center">Rate</div>
							<div class="header-cell text-center">QR / Typed</div>
							<div class="header-cell text-center">Avg days</div>
						</div>
						<div class="table-row runs-grid" v-for="run in report.runs" :key="run.id">
							<div class="table-cell">
								<router-link :to="{ name: 'autoCampaignRunReport', params: { runId: run.id } }" class="recipient-main">
									{{ run.code || `Run #${run.id}` }}
								</router-link>
							</div>
							<div class="table-cell">{{ formatDate(run.sent_at || run.created_at) }}</div>
							<div class="table-cell text-center">{{ run.mailed }}</div>
							<div class="table-cell text-center">{{ run.visited }}</div>
							<div class="table-cell text-center">
								<div class="rate-bar" :title="formatPercent(run.response_rate)">
									<div class="rate-bar-fill" :style="{ width: Math.min(run.response_rate, 100) + '%' }"></div>
								</div>
								<small>{{ formatPercent(run.response_rate) }}</small>
							</div>
							<div class="table-cell text-center">{{ run.visited_qr }} / {{ run.visited_typed }}</div>
							<div class="table-cell text-center">{{ run.avg_days_to_visit === null ? '–' : run.avg_days_to_visit }}</div>
						</div>
					</div>
					<div v-else class="empty-state">No tracked mail has gone out for this campaign yet.</div>
				</div>

				<div class="report-card mt-4">
					<div class="report-card-header">
						<h3 class="report-card-title">Visits</h3>
						<div class="d-flex gap-2 flex-wrap">
							<select v-if="!isRegular" v-model="filters.run" class="form-select form-select-sm filter-select" aria-label="Filter by run">
								<option value="">All runs</option>
								<option v-for="run in report.runs" :key="run.id" :value="run.id">{{ run.code || `Run #${run.id}` }}</option>
							</select>
							<select v-model="filters.channel" class="form-select form-select-sm filter-select" aria-label="Filter by channel">
								<option value="">All channels</option>
								<option value="qr">QR scan</option>
								<option value="typed">Typed link</option>
								<option value="unknown">Unknown</option>
							</select>
						</div>
					</div>
					<div v-if="filteredVisits.length" class="activity-table">
						<div class="table-header visits-grid" :class="{ 'visits-grid--no-run': isRegular }">
							<div class="header-cell">Recipient</div>
							<div v-if="!isRegular" class="header-cell">Run</div>
							<div class="header-cell">Visited</div>
							<div class="header-cell">Channel</div>
							<div class="header-cell">Location</div>
							<div class="header-cell">Landing page</div>
						</div>
						<div class="table-row visits-grid" :class="{ 'visits-grid--no-run': isRegular }" v-for="visit in filteredVisits" :key="visit.id">
							<div class="table-cell">
								<router-link
									v-if="visit.municipality_id && !isRegular"
									:to="{ name: 'autoCampaignMunicipalityReport', params: { campaignId, runId: visit.run_id, municipalityId: visit.municipality_id } }"
									class="recipient-main"
								>
									{{ visit.name }}
								</router-link>
								<div v-else class="recipient-main">{{ visit.name }}</div>
								<div class="recipient-sub">{{ [visit.city, visit.state].filter(Boolean).join(', ') }}</div>
							</div>
							<div v-if="!isRegular" class="table-cell">{{ visit.run_code || `Run #${visit.run_id}` }}</div>
							<div class="table-cell">
								{{ formatDateTime(visit.visited_at) }}
								<div v-if="visit.delivered_at" class="recipient-sub">{{ daysAfterDelivery(visit) }}</div>
								<div v-if="visit.visits > 1" class="recipient-sub">{{ visit.visits }} visits · last {{ formatDateTime(visit.last_visited_at) }}</div>
								<button type="button" class="timeline-link" @click="openTimeline(visit)"><i class="fas fa-stream me-1"></i>Timeline</button>
							</div>
							<div class="table-cell">
								<span class="status-pill" :class="`channel-${visit.channel || 'unknown'}`">{{ channelLabel(visit.channel) }}</span>
							</div>
							<div class="table-cell" :title="visit.location ? 'Approximate, from the IP address' : ''">{{ visit.location || '–' }}</div>
							<div class="table-cell landing-cell" :title="visit.page_url">{{ visit.page_url || '–' }}</div>
						</div>
					</div>
					<div v-else class="empty-state">{{ report.visits.length ? 'No visits match the filters.' : 'No visits recorded yet.' }}</div>
				</div>
			</div>
		</div>

		<div class="modal fade" tabindex="-1" aria-labelledby="responseTimelineLabel" aria-hidden="true" ref="timelineModal">
			<div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
				<div class="modal-content">
					<div class="modal-header">
						<div>
							<h5 class="modal-title" id="responseTimelineLabel">Visit timeline</h5>
							<small v-if="timelineVisit" class="text-muted">
								{{ timelineVisit.name }}<template v-if="timelineVisit.unique_url"> · <code>{{ timelineVisit.unique_url }}</code></template>
							</small>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<ShortlinkVisitTimeline v-if="timelineVisit" :key="timelineVisit.id" :type="isRegular ? 'delivery_object' : 'auto_item'" :id="timelineVisit.id" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { Modal } from 'bootstrap'
import restApi from '@/api/index'
import ShortlinkVisitTimeline from '@/components/campaign/ShortlinkVisitTimeline.vue'
import { formatUtc, utcToLocal } from '@/utils/time'

const EMPTY_TOTALS = {
	mailed: 0,
	visited: 0,
	total_visits: 0,
	visited_qr: 0,
	visited_typed: 0,
	visited_unknown: 0,
	response_rate: 0,
	avg_days_to_visit: null,
}

export default {
	name: 'AutoCampaignResponseReport',
	components: { ShortlinkVisitTimeline },
	data() {
		return {
			loading: false,
			savingSlack: false,
			timelineModal: null,
			// Visit row whose recipient timeline is open
			timelineVisit: null,
			report: {
				campaign: {},
				totals: { ...EMPTY_TOTALS },
				runs: [],
				visits: [],
			},
			filters: {
				run: '',
				channel: '',
			},
		}
	},
	computed: {
		campaignId() {
			return this.$route.params.campaignId
		},
		// The same report serves regular (one-off) campaigns, which have no runs.
		isRegular() {
			return this.$route.name === 'campaignResponseReport'
		},
		backRoute() {
			return this.isRegular ? { name: 'campaignsEdit', params: { id: this.campaignId } } : { name: 'autoCampaignRuns', params: { id: this.campaignId } }
		},
		totals() {
			return this.report.totals
		},
		filteredVisits() {
			return this.report.visits.filter(visit => {
				if (this.filters.run && String(visit.run_id) !== String(this.filters.run)) return false
				if (this.filters.channel && (visit.channel || 'unknown') !== this.filters.channel) return false
				return true
			})
		},
	},
	watch: {
		campaignId() {
			this.loadReport()
		},
	},
	async mounted() {
		this.timelineModal = new Modal(this.$refs.timelineModal)
		await this.loadReport()
	},
	beforeDestroy() {
		if (this.timelineModal) this.timelineModal.dispose()
	},
	methods: {
		openTimeline(visit) {
			this.timelineVisit = visit
			this.timelineModal.show()
		},
		async toggleSlack(enabled) {
			this.savingSlack = true
			try {
				await restApi.put(`/${this.isRegular ? 'campaigns' : 'auto-campaigns'}/${this.campaignId}/shortlink-slack`, { enabled })
				this.report.campaign = { ...this.report.campaign, shortlink_slack_notify: enabled }
			} catch (error) {
				this.$swal.fire({ icon: 'error', title: error.response?.data?.message || 'Could not update Slack notifications.', timer: 5000 })
			} finally {
				this.savingSlack = false
			}
		},
		async loadReport() {
			this.loading = true
			try {
				const { data } = await restApi.get(`/${this.isRegular ? 'campaigns' : 'auto-campaigns'}/${this.campaignId}/response-report`)
				this.report = {
					campaign: data?.campaign || {},
					totals: { ...EMPTY_TOTALS, ...(data?.totals || {}) },
					runs: data?.runs || [],
					visits: data?.visits || [],
				}
			} catch (error) {
				console.error('Error loading campaign response report:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Failed to load response report',
					text: error.response?.data?.message || error.message || 'An error occurred while loading the report.',
				})
			} finally {
				this.loading = false
			}
		},
		channelLabel(channel) {
			if (channel === 'qr') return 'QR scan'
			if (channel === 'typed') return 'Typed link'
			return 'Unknown'
		},
		daysAfterDelivery(visit) {
			const days = Math.max(0, utcToLocal(visit.visited_at).diff(utcToLocal(visit.delivered_at), 'days'))
			return days === 0 ? 'same day as delivery' : `${days} day${days === 1 ? '' : 's'} after delivery`
		},
		formatPercent(value) {
			return `${parseFloat(value || 0).toFixed(1)}%`
		},
		formatDate(value) {
			return value ? formatUtc(value, 'MMM D, YYYY') : 'N/A'
		},
		formatDateTime(value) {
			return value ? formatUtc(value, 'MMM D, YYYY h:mm A') : 'N/A'
		},
	},
}
</script>

<style scoped>
.campaign-response-report-page {
	padding-bottom: 32px;
}

.back-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	padding: 0;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
}

.back-btn:hover:not(:disabled) {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.back-btn:disabled {
	opacity: 0.6;
	cursor: default;
}

.page-title {
	color: #111827;
	margin: 0 0 4px 0;
}

.page-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

.loading-state,
.empty-state {
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 16px;
	padding: 48px 24px;
	text-align: center;
}

.overview-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
	gap: 16px;
}

.metric-card,
.report-card {
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 16px;
	padding: 20px;
	box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}

.metric-value {
	font-size: 2rem;
	font-weight: 700;
	line-height: 1;
}

.metric-divider {
	margin: 0 6px;
	color: #cbd5e1;
	font-weight: 400;
}

.metric-label {
	margin-top: 8px;
	color: #6b7280;
	font-size: 0.95rem;
}

.report-card-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px;
	margin-bottom: 16px;
}

.report-card-title {
	margin: 0;
	font-size: 1.1rem;
	font-weight: 700;
}

.report-card-meta {
	color: #6b7280;
	font-size: 0.85rem;
}

.filter-select {
	width: auto;
	min-width: 140px;
}

.activity-table {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.table-header,
.table-row {
	display: grid;
	align-items: center;
	gap: 12px;
}

.runs-grid {
	grid-template-columns: 1.2fr 1fr repeat(5, minmax(0, 0.8fr));
}

.visits-grid {
	grid-template-columns: 1.5fr 0.8fr 1.3fr 0.8fr 1.1fr 1.6fr;
}

.visits-grid--no-run {
	grid-template-columns: 1.5fr 1.3fr 0.8fr 1.1fr 1.6fr;
}

.table-header {
	color: #64748b;
	font-size: 0.8rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.table-row {
	background: #f8fafc;
	border-radius: 12px;
	padding: 14px;
}

.table-cell {
	min-width: 0;
	word-break: break-word;
}

.landing-cell {
	overflow: hidden;
	color: #64748b;
	font-size: 0.85rem;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.text-center {
	text-align: center;
}

.timeline-link {
	margin-top: 4px;
	padding: 0;
	border: 0;
	background: transparent;
	color: #315efb;
	font-size: 12px;
	font-weight: 600;
}

.slack-toggle {
	font-size: 13px;
	color: #475569;
}

.recipient-main {
	font-weight: 600;
}

.recipient-sub {
	color: #6b7280;
	font-size: 0.85rem;
	margin-top: 4px;
}

.rate-bar {
	height: 6px;
	margin: 0 auto 4px;
	max-width: 80px;
	overflow: hidden;
	border-radius: 999px;
	background: #e2e8f0;
}

.rate-bar-fill {
	height: 100%;
	border-radius: 999px;
	background: #198754;
}

.status-pill {
	display: inline-flex;
	align-items: center;
	padding: 6px 10px;
	border-radius: 999px;
	font-size: 0.8rem;
	font-weight: 700;
}

.channel-qr {
	background: #dbeafe;
	color: #1d4ed8;
}

.channel-typed {
	background: #dcfce7;
	color: #166534;
}

.channel-unknown {
	background: #f1f5f9;
	color: #64748b;
}

@media (max-width: 991px) {
	.runs-grid,
	.visits-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.table-header {
		display: none;
	}
}
</style>
